import { useKeycloak } from '@nxtpeople/nxt-vue-keycloak';
import * as Sentry from '@sentry/vue';
import { Settings } from 'luxon';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { UserInfo } from '../collections/user-info/user-info.interface';
import router from '../router';
import { useIndexedDb } from './useIndexedDb';
import { useStore } from './useStore';

export function useApp() {
   const { state, dispatch } = useStore();
   const { locale } = useI18n();
   const { clearCacheAndRefresh } = useIndexedDb();
   const { t } = useI18n();
   const route = useRoute();
   const { registerWebSockets, unregisterEcho } = useWebSockets();

   //#region Global Data
   const userInfo = computed(() => state.userInfo?.userInfo);
   const loadingUserInfo = computed(() => state.userInfo?.loadingUserInfo);
   watch(userInfo, fetchGlobalData, { immediate: true });
   watch(userInfo, registerWebSockets, { immediate: true });

   async function fetchGlobalData(userInfo?: UserInfo) {
      if (!userInfo) return;

      try {
         state.userInfo!.loadingUserInfo = true;
         await Promise.allSettled([dispatch('communicationLanguage/getCommunicationLanguages'), dispatch('module/getModules')]);
      } catch (ex) {
         Sentry.captureException(ex);
         router.push({
            path: '/error',
            query: {
               error_code: '400',
               error_title: t('errors.error'),
               error_message: t('errors.unknown-error'),
               error_navigate_text: t('actions.refresh'),
               error_navigate_path: window.location.origin,
            },
         });
      } finally {
         state.userInfo!.loadingUserInfo = false;
      }
   }

   onUnmounted(unregisterEcho);
   //#endregion

   //#region Keycloak
   const { isAuthenticated } = useKeycloak();

   async function handleAuthChange(isAuthenticated: boolean) {
      if (isAuthenticated) {
         return await fetchGlobalData();
      }
   }

   watch(isAuthenticated, handleAuthChange, { immediate: true });
   //#endregion

   //#region User Info
   function handleLocaleChange(newLocale: string) {
      if (newLocale) {
         Settings.defaultLocale = newLocale.toLowerCase();
         document.documentElement.lang = newLocale.toLowerCase();
         if (route.meta.titleKey) {
            document.title = `${t('app-name')} - ${t(route.meta.titleKey as string)}`;
         }
      }
   }

   function handleUserChange(userInfo?: UserInfo, oldUserInfo?: UserInfo) {
      if (userInfo) {
         Sentry.setUser({ username: `${userInfo.first_name} ${userInfo.last_name}`, email: userInfo.email, id: userInfo.id.toString() });
         if (oldUserInfo) clearCacheAndRefresh();
         locale.value = userInfo.communication_language?.iso_639_1.toLowerCase() || 'nl';
      } else {
         Sentry.configureScope((scope) => scope.setUser(null));
      }
   }
   //#endregion

   return { handleLocaleChange, handleUserChange, loading: loadingUserInfo };
}
